import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { validEmailValidator } from '@app/components/commons/forms/formly-validators';
import { OtpService } from '@app/services/authenticators/otp.service';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { GlobalState } from '@app/store/store';
import { Button, CardLabel } from '@infocert/ng-ui-kit/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom, tap } from 'rxjs';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  standalone: false,
})
@UntilDestroy()
export class ReportPageComponent implements OnInit {
  constructor(
    private store: Store<GlobalState>,
    private certificateService: CertificateService,
    private otpService: OtpService,
    private translateService: TranslateService
  ) {}

  loading: boolean = false;
  showSuccess: boolean = false;

  activeReport: boolean = false;
  isPkBox: boolean = false;
  isIcss: boolean = false;
  email: string = '';
  mode: string = '';

  successText: string = '';

  showReportModal: boolean = false;
  showEmailModal: boolean = false;
  showEmailModalStep2: boolean = false;
  emailStep1Attempted = false;
  emailStep2Attempted = false;

  get iconText(): string {
    return this.activeReport ? 'COMMON.ACTIVE' : 'COMMON.NEEDS_ACTIVATE';
  }

  get buttonText(): string {
    return this.activeReport ? 'COMMON.DISACTIVATE' : 'COMMON.ACTIVATE';
  }

  get cardReportButton(): Button {
    const label = this.translateService.instant(
      'SUSPEND_PAGE.AUTOMATIC_CARD.BUTTON_TEXT',
      {
        state: this.translateService.instant(
          this.activeReport ? 'COMMON.DISACTIVATE' : 'COMMON.ACTIVATE'
        ),
      }
    );

    return {
      label,
      altText: label,
      color: 'text',
      onClick: () => {
        this.showModal('manage_report');
        this.openModal('RepModal');
      },
    };
  }

  get cardEmailButton(): Button {
    const changeLabel = this.translateService.instant('COMMON.BUTTON.CHANGE');
    return {
      label: changeLabel,
      altText: changeLabel,
      color: 'text',
      onClick: () => {
        this.showModal('change_email');
        this.openModal('EmailModal');
      },
    };
  }

  get cardEmailLabel(): CardLabel[] {
    return [
      {
        label: this.translateService.instant(
          'REPORT_PAGE.PK_BOX.CHANGE_EMAIL_CARD.TITLE'
        ),
        value: this.email,
      },
    ];
  }

  ngOnInit(): void {
    // Dopo un tentativo di submit, nasconde i validator mentre l'utente digita.
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.emailStep1Attempted) {
        this.emailStep1Attempted = false;
      }
      if (this.emailStep2Attempted) {
        this.emailStep2Attempted = false;
      }
    });

    this.store
      .pipe(
        untilDestroyed(this),
        tap(store => {
          if (store.session) {
            this.isIcss = store.session.icssUser;
            if (!this.isIcss && !store.session?.trissUser) {
              this.isPkBox = true;
            }
          }

          if (store.certificate) {
            this.email = store.certificate.email;
          }
        })
      )
      .subscribe();

    this.getDailyReport();
  }

  emailField = new UntypedFormControl('', [
    Validators.required,
    validEmailValidator,
  ]);

  otp = new UntypedFormControl('', [
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  form = new UntypedFormGroup({
    emailField: this.emailField,
    otp: this.otp,
  });

  // convenience getter for easy access to form fields
  get f() {
    return this.form.controls;
  }

  get formErrors() {
    const errors = this.form.errors;
    return errors;
  }

  private getOtpDisclaimerText(): string {
    const rawValue = (this.otp?.value ?? '').toString().trim();
    if (!rawValue || rawValue === 'null') {
      return this.translateService.instant('COMMON.VALIDATORS.REQUIRED');
    }

    const errors = this.otp?.errors;
    if (!errors) return '';

    if (errors['minlength']) {
      return this.translateService.instant('COMMON.VALIDATORS.MIN_LENGTH');
    }
    if (errors['maxlength']) {
      return this.translateService.instant('COMMON.VALIDATORS.MAX_LENGTH');
    }
    if (errors['required']) {
      return this.translateService.instant('COMMON.VALIDATORS.REQUIRED');
    }
    return '';
  }

  getEmailOtpErrorDisclaimerText(): string {
    if (!this.emailStep2Attempted || this.otp.valid) return '';
    return this.getOtpDisclaimerText();
  }

  // CHANGE EMAIL
  onSubmit() {
    this.loading = true;
    this.ensureOtpStep2Validators();
    // stop here if form is invalid
    if (this.form.invalid) {
      this.emailStep2Attempted = true;
      this.form.markAllAsTouched();
      this.loading = false;
      return;
    }

    this.certificateService
      //@ts-ignore
      .changeEmail(
        this.f['emailField'].value.trim(),
        this.f['otp'].value.trim()
      )
      .subscribe({
        next: async response => {
          this.loading = false;

          // Se tutto va bene
          this.email = this.f['emailField'].value;
          this.clearForm();

          this.mode = 'change_email';
          this.showEmailModal = false;
          this.showEmailModalStep2 = false;
          this.closeModal();
          this.showSuccess = true;
        },
        error: error => {
          console.error(error);
          this.goToEmailStepOne();
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  async getDailyReport() {
    const reportStatus = await lastValueFrom(
      this.certificateService.getDailyReport()
    );

    this.activeReport = reportStatus.active;
  }

  showModal(mode: string) {
    switch (mode) {
      case 'manage_report':
        this.showReportModal = true;
        break;
      case 'change_email':
        this.f['emailField'].setValue(this.email);
        this.showEmailModal = true;
        break;
    }
  }

  manageReport() {
    this.loading = true;

    let payload = !this.activeReport;

    this.certificateService
      //@ts-ignore
      .updateDailyReport(payload.toString())
      .subscribe({
        next: async response => {
          this.loading = false;

          // Se tutto va bene
          if (this.activeReport) {
            this.successText = 'COMMON.DISACTIVATED';
          } else {
            this.successText = 'COMMON.ACTIVATED';
          }

          this.getDailyReport();

          this.showReportModal = false;
          this.closeModal();
          this.mode = 'manage_report';
          this.showSuccess = true;
        },
        error: error => {
          console.error(error);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  goToEmailSecondStep() {
    if (this.form.invalid) {
      this.emailStep1Attempted = true;
      this.form.markAllAsTouched();
      return;
    }
    this.emailStep1Attempted = false;
    this.sendOtp();
    this.ensureOtpStep2Validators();

    this.showEmailModal = false;
    this.showEmailModalStep2 = true;
  }

  goToEmailStepOne() {
    this.otpService.resetOtpInputVal(true);
    this.clearForm();
    this.showEmailModal = true;
    this.showEmailModalStep2 = false;
  }

  clearForm() {
    this.emailStep1Attempted = false;
    this.emailStep2Attempted = false;

    this.emailField.setValidators([Validators.required, validEmailValidator]);
    this.emailField.updateValueAndValidity({ emitEvent: false });

    this.otp.clearValidators();
    this.otp.setValidators([
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);
    this.otp.updateValueAndValidity({ emitEvent: false });

    this.emailField.setValue(this.email, { emitEvent: true });
    this.otp.setValue('', { emitEvent: true });
  }

  private ensureOtpStep2Validators(): void {
    this.otp.setValidators([
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);
    this.otp.updateValueAndValidity({ emitEvent: false });
  }

  sendOtp() {
    this.otpService.generateCode().subscribe({
      next: async response => {
        // Se tutto va bene
      },
      error: error => {
        console.error(error);
      },
      complete: () => {},
    });
  }

  public repModalVisible = false;
  public emailModalVisible = false;
  public renderRepModalContent = false;
  public renderEmailModalContent = false;

  openModal(name: string = '') {
    if (name === 'RepModal') {
      this.renderRepModalContent = true;
      this.repModalVisible = true;
    } else if (name === 'EmailModal') {
      this.goToEmailStepOne();
      this.renderEmailModalContent = true;
      this.emailModalVisible = true;
    }
  }

  onToggleRepModal(visible: boolean): void {
    this.repModalVisible = visible;
    if (!visible) {
      this.closeModal();
    }
  }

  onToggleEmailModal(visible: boolean): void {
    this.emailModalVisible = visible;
    if (!visible) {
      this.closeModal();
    }
  }

  closeModal() {
    this.showReportModal = false;
    this.repModalVisible = false;
    this.emailModalVisible = false;
    if (this.renderEmailModalContent) {
      this.goToEmailStepOne();
    }
    this.renderRepModalContent = false;
    this.renderEmailModalContent = false;
  }
}
